import { fnv1a, mulberry32 } from "./avatar-random"

/**
 * Generated colours for the drug avatar (recipe 2.0.0).
 *
 * Each seed draws a main hue anywhere on the wheel and a second hue offset
 * from it, and a fixed OKLCH lightness/chroma curve turns the pair into the
 * four-stop ramp the sphere paints with. OKLCH keeps equal lightness steps
 * looking equal across hues, so every ramp has the same clean dark-to-light
 * shape. Per-seed tone and chroma shifts keep drugs apart.
 *
 * Colours come from their own draw stream, separate from the composition RNG
 * in avatar-recipe.ts, so tuning colour never moves a cloud.
 */

/**
 * Seeds the colour draw stream. Frozen at the value the 2.0.0 colours were
 * approved under in the colour lab; changing it reshuffles every drug's
 * colours. Never change this.
 */
const COLOUR_SEED_NAMESPACE = "colour-lab-1"

/** Main hues in this band read as olive at mid lightness; skipped when `skipOlive` is on. */
export const OLIVE_BAND: readonly [number, number] = [80, 120]

export type ColourSettings = {
  /** 0..1, how saturated the ramp is. */
  vividness: number
  /** 0..1, how dark the deepest stop goes. */
  depth: number
  /** 0..1, how close to white the lightest stop sits. */
  glow: number
  /** 0..1, the furthest the second hue may sit from the main hue (0 = one colour, 1 = near opposite). Each seed draws its own distance up to this. */
  accentSpread: number
  /** 0..1, how much lightness and saturation differ from seed to seed. */
  variety: number
  skipOlive: boolean
}

/**
 * The production colours, pinned. A change here (or to the algorithm) without
 * an AVATAR_RECIPE_VERSION bump fails scripts/test-drug-avatar-fixture.ts.
 */
export const PRODUCTION_COLOUR_SETTINGS: Readonly<ColourSettings> = Object.freeze({
  vividness: 0.7,
  depth: 0.5,
  glow: 0.6,
  accentSpread: 0.8,
  variety: 0.8,
  skipOlive: true,
})

export type ColourRamp = readonly [string, string, string, string]

export type ColourPlan = {
  hue: number
  accentHue: number
  colours: ColourRamp
}

function clampUnit(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback
  return Math.min(1, Math.max(0, value))
}

export function normaliseColourSettings(settings: Partial<ColourSettings> = {}): ColourSettings {
  const fallback = PRODUCTION_COLOUR_SETTINGS
  return {
    vividness: clampUnit(settings.vividness, fallback.vividness),
    depth: clampUnit(settings.depth, fallback.depth),
    glow: clampUnit(settings.glow, fallback.glow),
    accentSpread: clampUnit(settings.accentSpread, fallback.accentSpread),
    variety: clampUnit(settings.variety, fallback.variety),
    skipOlive: typeof settings.skipOlive === "boolean" ? settings.skipOlive : fallback.skipOlive,
  }
}

function wrapHue(hue: number): number {
  return ((hue % 360) + 360) % 360
}

/** Circular midpoint of two hues, taking the short way round. */
function midHue(a: number, b: number): number {
  const delta = ((b - a + 540) % 360) - 180
  return wrapHue(a + delta / 2)
}

type Rgb = [number, number, number]

function oklabToLinearRgb(L: number, a: number, b: number): Rgb {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
}

function oklchToLinearRgb(L: number, C: number, h: number): Rgb {
  const radians = (h * Math.PI) / 180
  return oklabToLinearRgb(L, C * Math.cos(radians), C * Math.sin(radians))
}

function inGamut([r, g, b]: Rgb): boolean {
  const epsilon = 0.0001
  return r >= -epsilon && r <= 1 + epsilon && g >= -epsilon && g <= 1 + epsilon && b >= -epsilon && b <= 1 + epsilon
}

function linearToSrgbChannel(value: number): number {
  const clamped = Math.min(1, Math.max(0, value))
  return clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * clamped ** (1 / 2.4) - 0.055
}

function srgbToLinearChannel(value: number): number {
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

/**
 * OKLCH -> `#rrggbb`. Colours the screen cannot show keep their lightness and
 * hue and lose chroma until they fit (binary search), which avoids the hue
 * shifts that plain channel clipping causes.
 */
export function oklchToHex(L: number, C: number, h: number): string {
  let chroma = Math.max(0, C)
  if (!inGamut(oklchToLinearRgb(L, chroma, h))) {
    let low = 0
    let high = chroma
    for (let step = 0; step < 24; step += 1) {
      const mid = (low + high) / 2
      if (inGamut(oklchToLinearRgb(L, mid, h))) low = mid
      else high = mid
    }
    chroma = low
  }
  return `#${oklchToLinearRgb(L, chroma, h)
    .map((channel) => Math.round(linearToSrgbChannel(channel) * 255).toString(16).padStart(2, "0"))
    .join("")}`
}

/** `#rrggbb` -> OKLab [L, a, b]. */
export function hexToOklab(hex: string): [number, number, number] {
  const [r, g, b] = [1, 3, 5].map((index) => srgbToLinearChannel(parseInt(hex.slice(index, index + 2), 16) / 255))
  const l = Math.cbrt(0.4122214708 * r! + 0.5363325363 * g! + 0.0514459929 * b!)
  const m = Math.cbrt(0.2119034982 * r! + 0.6806995451 * g! + 0.1073969566 * b!)
  const s = Math.cbrt(0.0883024619 * r! + 0.2817188376 * g! + 0.6299787005 * b!)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

function drawMainHue(unit: number, skipOlive: boolean): number {
  if (!skipOlive) return unit * 360
  const [from, to] = OLIVE_BAND
  const hue = unit * (360 - (to - from))
  return hue >= from ? hue + (to - from) : hue
}

/**
 * The seed's colours under the given settings. Stops run dark to light, the
 * order the sphere expects: [deep, mid, accent, light].
 */
export function colourPlanForSeed(seed: string, settings: Partial<ColourSettings> = PRODUCTION_COLOUR_SETTINGS): ColourPlan {
  const safe = normaliseColourSettings(settings)
  const random = mulberry32(fnv1a(`${COLOUR_SEED_NAMESPACE}|${String(seed)}`))
  const hueUnit = random()
  const directionUnit = random()
  const spreadUnit = random()
  const toneUnit = random()
  const chromaUnit = random()

  const hue = drawMainHue(hueUnit, safe.skipOlive)
  const spread = 10 + spreadUnit * safe.accentSpread * 170
  const accentHue = wrapHue(hue + (directionUnit < 0.5 ? -spread : spread))
  // Per-seed shifts, centred on zero: tone moves the whole ramp lighter or
  // darker, chroma makes it richer or softer. Stop order never inverts.
  const tone = (toneUnit - 0.5) * 2 * 0.08 * safe.variety
  const chromaScale = 1 + (chromaUnit - 0.5) * 2 * 0.35 * safe.variety

  const vivid = safe.vividness
  const deep = oklchToHex(0.36 - safe.depth * 0.17 + tone * 0.5, (0.05 + vivid * 0.07) * chromaScale, hue)
  const mid = oklchToHex(0.55 + tone, (0.07 + vivid * 0.13) * chromaScale, hue)
  const accent = oklchToHex(0.73 + tone * 0.6, (0.06 + vivid * 0.12) * chromaScale, accentHue)
  const light = oklchToHex(0.88 + safe.glow * 0.08, 0.015 + vivid * 0.045, midHue(hue, accentHue))

  return { hue: Number(hue.toFixed(3)), accentHue: Number(accentHue.toFixed(3)), colours: [deep, mid, accent, light] }
}

export const _internals = {
  COLOUR_SEED_NAMESPACE,
  wrapHue,
  midHue,
  drawMainHue,
  inGamut,
  oklchToLinearRgb,
}
